import userPrepaidCardQueries from '@apis/queries/userPrepaidCardQueries';
import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import Divider from '@components/Divider';
import Header from '@components/Header';
import PrepaidProvider from '@components/prepaid/PrepaidProvider';
import useBeginCheckoutOnce from '@hooks/analytics/useBeginCheckoutOnce';
import useCurrency from '@hooks/item/useCurrency';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useQuery } from '@tanstack/react-query';
import { prepaidCardCheckoutToGtmFormat } from '@utils/gtm';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChargeAgreements from './components/ChargeAgreements';
import ChargeAmount from './components/ChargeAmount';
import ChargeCardSummary from './components/ChargeCardSummary';
import ChargePaymentInfo from './components/ChargePaymentInfo';
import PrepaidCardChargeFooter from './components/PrepaidCardChargeFooter';

interface Params {
    prepaidCardId: string;
    amount?: number;
}

declare module '@stackflow/config' {
    interface Register {
        PrepaidCardChargeActivity: Params;
    }
}

const PrepaidCardChargeActivity: ActivityComponentType<'PrepaidCardChargeActivity'> = ({ params }) => {
    const { t } = useTranslation('prepaidCard');
    const { prepaidCardId, amount } = params;
    const [agreed, setAgreed] = useState(false);
    const { data: card } = useQuery(userPrepaidCardQueries.detail(prepaidCardId));
    const currency = useCurrency();
    // 결제 실패·취소 후 복원 진입(amount 전달)은 발화하지 않는다
    const beginCheckout = useMemo(() => (
        card && amount === undefined
            ? prepaidCardCheckoutToGtmFormat({
                id: card.id, name: card.name, currency, source: 'PREPAID_CARD_CHARGE',
            })
            : undefined
    ), [card, amount, currency]);
    useBeginCheckoutOnce(beginCheckout);

    return (
        <AppScreen>
            <PrepaidProvider>
                <CtaLayout header={<Header title={t('charge.title')} />}
                    footer={<PrepaidCardChargeFooter id={prepaidCardId} agreed={agreed} />}>
                    <Column fullWidth>
                        <ChargeCardSummary cardId={prepaidCardId} />
                        <Divider />
                        <ChargeAmount cardId={prepaidCardId} initialAmount={amount} />
                        <Divider/>
                        <ChargePaymentInfo cardId={prepaidCardId} />
                        <Divider/>
                        <ChargeAgreements agreed={agreed} onAgreedChange={setAgreed} />
                    </Column>
                </CtaLayout>
            </PrepaidProvider>
        </AppScreen>
    );
};

export default PrepaidCardChargeActivity;
